import { Eye, Rocket, Trash2, ExternalLink, FileEdit, Handshake } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import type { CloserLead } from '@/types'
import type { CloserLeadsFilters } from '@/hooks/useCloserLeads'
import { CloserLeadsTableHeader } from '@/components/CloserLeads/CloserLeadsTableHeader'
import { isLeadPendingProposalElaboration } from '@/components/CloserLeads/closerLeadProposalFlow.util'
import { dealsPagePathForDeal } from '@/utils/dealsPageNavigation'
import { negotiationLockNotice } from '@/components/CloserLeads/negotiationLockNotice'

const statusConfig: Record<string, { label: string; color: string }> = {
  NOVO: { label: 'Novo', color: '#3b82f6' },
  EM_CONTATO: { label: 'Em Contato', color: '#f59e0b' },
  QUALIFICADO: { label: 'Qualificado', color: '#10b981' },
  AGUARDANDO: { label: 'Aguardando', color: '#8b5cf6' },
  CONVERTIDO: { label: 'Convertido', color: '#22c55e' },
  DESCARTADO: { label: 'Descartado', color: '#ef4444' },
  PERDIDO: { label: 'Perdido', color: '#ef4444' },
  NUTRICAO: { label: 'Nutrição', color: '#06b6d4' },
}

function getTemperature(score: number) {
  if (score >= 80) return { label: 'Quente', color: '#ef4444' }
  if (score >= 50) return { label: 'Morno', color: '#f59e0b' }
  return { label: 'Frio', color: '#0ea5e9' }
}

function getStatusInfo(status: string) {
  return statusConfig[status?.toUpperCase()] || { label: status || '—', color: '#64748b' }
}

interface CloserLeadsTableProps {
  leads: CloserLead[]
  loading: boolean
  isRefreshing?: boolean
  filters: CloserLeadsFilters
  onOpenLead: (lead: CloserLead) => void
  onConvertToDeal: (lead: CloserLead) => void
  onStartNewNegotiation?: (lead: CloserLead) => void
  onResumeProposal?: (lead: CloserLead) => void
  onOpenDiscard?: (lead: CloserLead) => void
  onDiscardLocked?: (lead: CloserLead) => void
  onDeleteLead?: (leadId: string) => void
  isAdminOrManager: boolean
  currentUserId?: string
}

const REMOVABLE_STATUSES = new Set(['DESCARTADO', 'PERDIDO', 'NUTRICAO', 'CONVERTIDO'])

export function CloserLeadsTable({
  leads,
  loading,
  isRefreshing = false,
  filters,
  onOpenLead,
  onConvertToDeal,
  onStartNewNegotiation,
  onResumeProposal,
  onOpenDiscard,
  onDiscardLocked,
  onDeleteLead,
  isAdminOrManager,
  currentUserId,
}: CloserLeadsTableProps) {
  const { sortField, setSortField } = filters
  const navigate = useNavigate()
  const showSkeleton = loading && leads.length === 0

  return (
    <div className='glass-card relative min-h-[280px] overflow-hidden p-0'>
      {isRefreshing && (
        <div
          className='absolute inset-x-0 top-0 z-20 h-0.5 overflow-hidden bg-border-subtle'
          aria-hidden
        >
          <div className='h-full w-full animate-pulse bg-accent' />
        </div>
      )}

      <div className={`overflow-x-auto transition-opacity duration-200 ${isRefreshing ? 'opacity-60' : ''}`}>
        <table className='w-full text-left'>
          <CloserLeadsTableHeader sortField={sortField} onSort={setSortField} />
          <tbody className='[&_tr:first-child_td]:pt-4'>
            {showSkeleton ? (
              Array.from({ length: 5 }).map((_, i) => (
                <tr key={i} className='border-b border-white/[0.03] animate-pulse'>
                  <td className='p-4'><div className='flex items-center gap-3'><div className='w-9 h-9 rounded-xl bg-white/[0.05]' /><div className='space-y-2'><div className='h-3 w-28 bg-white/[0.05] rounded' /><div className='h-2 w-20 bg-white/[0.03] rounded' /></div></div></td>
                  <td className='p-4 hidden md:table-cell'><div className='h-3 w-24 bg-white/[0.05] rounded' /></td>
                  <td className='p-4'><div className='h-5 w-16 bg-white/[0.05] rounded-lg' /></td>
                  <td className='p-4 hidden lg:table-cell'><div className='h-5 w-14 bg-white/[0.05] rounded-lg' /></td>
                  <td className='p-4 hidden xl:table-cell'><div className='h-3 w-20 bg-white/[0.05] rounded' /></td>
                  <td className='p-4'><div className='flex gap-1.5'><div className='w-8 h-8 bg-white/[0.05] rounded-lg' /><div className='w-8 h-8 bg-white/[0.05] rounded-lg' /></div></td>
                </tr>
              ))
            ) : leads.length === 0 ? (
              <tr>
                <td colSpan={6} className='p-16 text-center text-text-muted font-bold uppercase tracking-wider'>
                  Nenhum lead encontrado
                </td>
              </tr>
            ) : (
              leads.map(lead => {
                const status = getStatusInfo(lead.status)
                const temp = getTemperature(lead.score || 50)

                return (
                  <tr
                    key={lead.id}
                    onClick={() => onOpenLead(lead)}
                    className='group hover:bg-accent/[0.02] transition-all cursor-pointer border-b border-white/[0.03]'
                  >
                    <td className='p-4'>
                      <div className='flex items-center gap-3'>
                        <div className='w-9 h-9 rounded-xl bg-accent/10 border border-accent/20 flex items-center justify-center text-accent font-bold text-xs shrink-0'>
                          {(lead.empresa || '?')[0].toUpperCase()}
                        </div>
                        <div className='min-w-0'>
                          <p className='font-bold text-sm text-text-primary truncate group-hover:text-accent transition-colors'>
                            {lead.empresa}
                          </p>
                          <p className='text-[11px] text-text-muted truncate'>
                            {lead.contato || 'Sem contato'}
                          </p>
                          {lead.exclusividade ? (
                            <p className='mt-1 text-[11px] font-medium text-amber-300'>
                              {negotiationLockNotice(lead.exclusividade)}
                            </p>
                          ) : null}
                        </div>
                      </div>
                    </td>
                    <td className='p-4 hidden md:table-cell'>
                      <span className='text-sm text-text-primary'>
                        {lead.exclusividade ? 'Oculto' : (lead.telefone || '—')}
                      </span>
                    </td>
                    <td className='p-4'>
                      <span
                        className='inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-bold uppercase tracking-wider border'
                        style={{
                          background: `${status.color}15`,
                          color: status.color,
                          borderColor: `${status.color}30`,
                        }}
                      >
                        <span className='w-1.5 h-1.5 rounded-full animate-pulse' style={{ backgroundColor: status.color }} />
                        {status.label}
                      </span>
                    </td>
                    <td className='p-4 hidden lg:table-cell'>
                      <span
                        className='text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-lg border'
                        style={{
                          color: temp.color,
                          background: `${temp.color}15`,
                          borderColor: `${temp.color}30`,
                        }}
                      >
                        {temp.label}
                      </span>
                    </td>
                    <td className='p-4 hidden xl:table-cell'>
                      <span className='text-xs text-text-muted'>
                        {lead.proximaAcao || lead.ultimaAcao || '—'}
                      </span>
                    </td>
                    <td className='p-4'>
                      <div className='flex gap-1.5'>
                        <button
                          className='p-2 rounded-lg bg-[var(--glass-bg)] border border-white/[0.06] text-text-muted hover:text-accent hover:border-accent/40 transition-all'
                          title='Ver detalhes'
                          onClick={e => { e.stopPropagation(); onOpenLead(lead) }}
                        >
                          <Eye size={16} />
                        </button>
                        {lead.exclusividade ? (
                          <button
                            className='flex h-8 items-center rounded-lg border border-red-500/20 bg-red-500/10 px-2.5 text-[11px] font-bold text-red-300 hover:bg-red-500/20'
                            title='Descartar lead'
                            onClick={e => { e.stopPropagation(); onDiscardLocked?.(lead) }}
                          >
                            Descartar
                          </button>
                        ) : isLeadPendingProposalElaboration(lead) && onResumeProposal ? (
                          <button
                            className='flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg border border-amber-500/25 bg-amber-500/10 p-2 text-amber-400 transition-all hover:bg-amber-500/20'
                            title='Continuar proposta'
                            onClick={e => { e.stopPropagation(); onResumeProposal(lead) }}
                          >
                            <FileEdit size={16} />
                          </button>
                        ) : lead.status === 'CONVERTIDO' ? (
                          <>
                            {lead.dealId ? (
                              <button
                                className='flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg border border-emerald-500/20 bg-emerald-500/10 p-2 text-emerald-400 transition-all hover:bg-emerald-500/20'
                                title='Ver Negociação'
                                onClick={e => { e.stopPropagation(); navigate(dealsPagePathForDeal(lead.dealId!)) }}
                              >
                                <ExternalLink size={16} />
                              </button>
                            ) : null}
                            {onStartNewNegotiation ? (
                              <button
                                className='flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg border border-[var(--color-success)]/20 bg-[var(--color-success)]/10 p-2 text-[var(--color-success)] transition-all hover:bg-[var(--color-success)]/20'
                                title='Nova negociação'
                                onClick={e => { e.stopPropagation(); onStartNewNegotiation(lead) }}
                              >
                                <Handshake size={16} />
                              </button>
                            ) : null}
                          </>
                        ) : (
                          <button
                            className='flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg border border-accent/20 bg-accent/10 p-2 text-accent transition-all hover:bg-accent/20'
                            title='Converter em negociação'
                            onClick={e => { e.stopPropagation(); onConvertToDeal(lead) }}
                          >
                            <Rocket size={16} />
                          </button>
                        )}
                        {!lead.exclusividade && (isAdminOrManager || lead.closerId === currentUserId) && (
                          <button
                            className='p-2 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 transition-all'
                            title={
                              REMOVABLE_STATUSES.has((lead.status || '').toUpperCase())
                                ? 'Excluir da carteira'
                                : 'Descartar lead'
                            }
                            onClick={e => {
                              e.stopPropagation()
                              const status = (lead.status || '').toUpperCase()
                              if (REMOVABLE_STATUSES.has(status)) {
                                onDeleteLead?.(lead.id)
                                return
                              }
                              onOpenDiscard?.(lead)
                            }}
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default CloserLeadsTable
